/**
 * Каркас админки — разметка 1:1 из legacy htdocs/core/templates/admin.tpl
 * (CSS-набор, <title>) и admin_inside.tpl (верхняя navbar + боковое меню).
 *
 * Соответствие legacy:
 *   .navbar-inverse.navbar-fixed-top          — шапка с бургером и логином справа
 *   .nav.nav-sidebar (visible-xs)             — мобильное меню (id=*_mob)
 *   .hidden-xs.col-sm-2.sidebar               — десктопное боковое меню
 *   группы 1/4/5/2/3 + группа 0 без заголовка — как в admin_inside.tpl
 *   h2.sub-header                             — заголовок раздела ($cur_head)
 *
 * Пункты меню — из таблицы modules (GET /api/admin/me → menu[].group),
 * названия групп — из modules_group (groups), верхнее меню — только суперадмину.
 * Ссылки сохраняют legacy-параметр ?group=N: он раскрывает нужную группу.
 *
 * jQuery/bootstrap.js нет — collapse групп и бургер сделаны на React-состоянии,
 * классы (.collapse/.in) и inline-стили остались легасёвые.
 */

import { useEffect, useState } from 'react'
import { Link, Outlet, useLocation, useNavigate, useSearchParams } from 'react-router-dom'

import { ADMIN_CSS, useDisablePublicCss, useStylesheets } from '@/lib/useStylesheets'
import { useApi } from '@/lib/useApi'
import { useSession } from '@/session/SessionProvider'

/** Пункт меню админки — Modules::adminMenu(): link = modules.module. */
interface AdminMenuLink {
  link: string
  name: string
  group: string
}

/** Ответ GET /api/admin/me (Handlers\Admin\Auth::me). */
interface AdminMe {
  admin: { id: number; login: string; superadmin: boolean }
  superadmin: boolean
  menu: AdminMenuLink[]
  top_menu: AdminMenuLink[]
  groups: Record<string, string>
  site: Record<string, string>
}

/**
 * Группы бокового меню в порядке и с id-шниками admin_inside.tpl.
 * name — фолбэк, если modules_group не отдал название.
 */
const MENU_GROUPS: ReadonlyArray<{ group: string; name: string; id: string; mobId: string }> = [
  { group: '1', name: 'Управление сайтом', id: 'site', mobId: 'site_mob' },
  { group: '4', name: 'Управление ЛК', id: 'lk', mobId: 'lk_mob' },
  { group: '5', name: 'Администрирование', id: 'admin', mobId: 'admin_mob' },
  { group: '2', name: 'Электроэнергия', id: 'electro', mobId: 'electro_mob' },
  { group: '3', name: 'Рассылки', id: 'rassll', mobId: 'rassll_mob' },
]

export function AdminLayout() {
  // legacy admin.tpl не подключает main.css — гасим его, иначе блоки квитанций
  // и истории в карточке участка съезжают вправо (лишний margin из main.css).
  useDisablePublicCss()
  useStylesheets(ADMIN_CSS)

  const { logoutAdmin } = useSession()
  const { data } = useApi<AdminMe>('/api/admin/me')
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()

  const [navOpen, setNavOpen] = useState(false)

  // legacy: $cur_link — сегмент после /admin (у «Главной» он пустой)
  const segments = location.pathname.split('/').filter((part) => part !== '')
  const curLink = segments[1] ?? ''
  // карточка участка (/admin/areas/13) — она рисует собственный заголовок
  const isAreaCard = segments[1] === 'areas' && segments[2] !== undefined

  const menu = data?.menu ?? []
  const topMenu = data?.top_menu ?? []
  const siteTitle = data?.site.site_title ?? ''

  /**
   * Раскрытая группа меню.
   *
   * legacy admin_inside.tpl брал её только из ?group=N. Но у вложенных страниц
   * (карточка участка /admin/areas/13) этого параметра в ссылке нет — меню
   * схлопывалось целиком и текущий раздел не подсвечивался.
   * Поэтому, если параметра нет, группу определяем по активному пункту меню.
   */
  const groupOfCurrent = menu.find((item) => item.link === curLink)?.group
  const group = searchParams.get('group') ?? groupOfCurrent ?? '0'

  const [openGroup, setOpenGroup] = useState(group)
  const [openGroupMob, setOpenGroupMob] = useState(group)

  useEffect(() => {
    setOpenGroup(group)
    setOpenGroupMob(group)
  }, [group])

  // legacy: $cur_head — название текущего пункта меню
  const curHead = [...menu, ...topMenu].find((item) => item.link === curLink)?.name ?? ''

  useEffect(() => {
    document.title = `Панель администратора ${siteTitle}`
  }, [siteTitle])

  const groupName = (id: string, fallback: string): string => data?.groups[id] ?? fallback

  const href = (item: AdminMenuLink): string => `/admin/${item.link}?group=${item.group}`

  /** legacy admin_inside.tpl: карточка участка подсвечивает пункт «areas». */
  const isActive = (item: AdminMenuLink): boolean =>
    item.link === curLink || (item.link === 'areas' && curLink === 'area')

  async function logoff(): Promise<void> {
    // гасит и токен доступа, и refresh на сервере, потом стирает их локально
    await logoutAdmin()
    // На главную САЙТА, а не на /admin/login: вышедший админ — обычный посетитель.
    navigate('/')
  }

  const itemsOf = (id: string): AdminMenuLink[] => menu.filter((item) => item.group === id)

  return (
    <>
      <style type="text/css">
        {`.navbar-fixed-bottom .navbar-collapse, .navbar-fixed-top .navbar-collapse {
    max-height: 100%;
}`}
      </style>

      <nav className="navbar navbar-inverse navbar-fixed-top">
        <div className="container-fluid">
          <div className="navbar-header">
            <button
              type="button"
              className={navOpen ? 'navbar-toggle' : 'navbar-toggle collapsed'}
              aria-expanded={navOpen}
              aria-controls="navbar"
              style={{ float: 'left', marginLeft: 20 }}
              onClick={() => setNavOpen((open) => !open)}
            >
              <span className="sr-only">Toggle navigation</span>
              <span className="icon-bar" />
              <span className="icon-bar" />
              <span className="icon-bar" />
            </button>
            <a className="navbar-brand" target="_blank" href="/" rel="noreferrer">
              {siteTitle}
            </a>
          </div>
          <div id="navbar" className={`navbar-collapse collapse${navOpen ? ' in' : ''}`}>
            {MENU_GROUPS.map((section) => (
              <div key={section.mobId}>
                <ul className="nav nav-sidebar visible-xs hidden-sm hidden-md hidden-lg">
                  <li>
                    <a
                      id={`href_${section.mobId}`}
                      style={{
                        backgroundColor: openGroupMob === section.group ? '#313131' : undefined,
                        color: '#888888',
                      }}
                      role="button"
                      aria-expanded={openGroupMob === section.group}
                      aria-controls={section.mobId}
                      onClick={() =>
                        setOpenGroupMob((open) => (open === section.group ? '' : section.group))
                      }
                    >
                      {groupName(section.group, section.name)}
                    </a>
                  </li>
                </ul>
                <ul
                  className={`nav nav-sidebar collapse ${
                    openGroupMob === section.group ? 'in ' : ''
                  }hidden-sm hidden-md hidden-lg`}
                  id={section.mobId}
                  role="tabpanel"
                  aria-labelledby="headingOne"
                >
                  {itemsOf(section.group).map((item) => (
                    <li key={item.link} className={isActive(item) ? 'active' : undefined}>
                      <Link
                        to={href(item)}
                        style={{
                          backgroundColor: isActive(item) ? '#313137' : undefined,
                          color: '#888888',
                        }}
                      >
                        {item.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}

            <ul className="nav nav-sidebar visible-xs hidden-sm hidden-md hidden-lg">
              {itemsOf('0').map((item) => (
                <li key={item.link} className={isActive(item) ? 'active' : undefined}>
                  <Link
                    to={href(item)}
                    style={{
                      backgroundColor: isActive(item) ? '#313137' : undefined,
                      color: '#888888',
                    }}
                  >
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>

            <ul className="nav navbar-nav navbar-right">
              {data?.superadmin === true &&
                topMenu.map((item) => (
                  <li key={item.link}>
                    <Link to={href(item)}>{item.name}</Link>
                  </li>
                ))}
              <li>
                <span className="add_left_pad">{data?.admin.login}</span>{' '}
                <a
                  className="no_left_pad"
                  href="/admin/logoff"
                  onClick={(event) => {
                    event.preventDefault()
                    void logoff()
                  }}
                >
                  (Выход)
                </a>
              </li>
            </ul>
          </div>
        </div>
      </nav>

      <div className="container-fluid cfluid">
        <div className="row">
          <div className="hidden-xs col-sm-2 sidebar">
            {MENU_GROUPS.map((section) => (
              <div key={section.id}>
                <ul className="nav nav-sidebar navbarfix">
                  <li>
                    <a
                      style={
                        openGroup === section.group ? { backgroundColor: '#eee' } : undefined
                      }
                      role="button"
                      aria-expanded={openGroup === section.group}
                      aria-controls={section.id}
                      onClick={() =>
                        setOpenGroup((open) => (open === section.group ? '' : section.group))
                      }
                    >
                      {groupName(section.group, section.name)}
                    </a>
                  </li>
                </ul>
                <ul
                  className={`nav nav-sidebar collapse${openGroup === section.group ? ' in' : ''}`}
                  id={section.id}
                  role="tabpanel"
                  aria-labelledby="headingOne"
                >
                  {itemsOf(section.group).map((item) => (
                    <li key={item.link} className={isActive(item) ? 'active' : undefined}>
                      <Link to={href(item)}>{item.name}</Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}

            <ul className="nav nav-sidebar navbarfix">
              {itemsOf('0').map((item) => (
                <li key={item.link} className={isActive(item) ? 'active' : undefined}>
                  <Link to={href(item)}>{item.name}</Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="col-xs-12 col-sm-10 col-sm-offset-2 main">
            {/* legacy admin_area.tpl: $('.sub-header')[0].remove() — карточка участка рисует свой заголовок */}
            {!isAreaCard && <h2 className="sub-header">{curHead}</h2>}
            <Outlet />
          </div>
        </div>
      </div>
    </>
  )
}
